const TEAM_INVITE_TOKEN_PATTERN =
  /^v1\.[A-Za-z0-9_-]{22}\.[A-Za-z0-9_-]{43}$/;

export function isTeamInviteToken(value: string): boolean {
  return TEAM_INVITE_TOKEN_PATTERN.test(value);
}

export function normalizeTeamNickname(value: string): string {
  return value.normalize("NFC").trim();
}

export function isTeamNickname(value: string): boolean {
  const normalized = normalizeTeamNickname(value);
  const length = Array.from(normalized).length;
  return !/[\p{Cc}\p{Cf}]/u.test(normalized) && length >= 2 && length <= 32;
}

export interface PendingTeamInviteJoin {
  nickname: string;
  idempotencyKey: string;
  userId?: string;
}

export function pendingTeamInviteJoinDisposition(
  pending: PendingTeamInviteJoin,
  authenticatedUserId: string,
): "auto-join" | "discard" {
  if (pending.userId === authenticatedUserId) return "auto-join";
  return "discard";
}

export function teamInvitePendingKey(token: string): string {
  return `cheemspay:team-invite:${token}`;
}

export function serializePendingTeamInviteJoin(
  nickname: string,
  idempotencyKey: string,
  userId?: string,
): string {
  return JSON.stringify(
    userId ? { nickname, idempotencyKey, userId } : { nickname, idempotencyKey },
  );
}

export function parsePendingTeamInviteJoin(
  raw: string | null,
  isNickname: (value: string) => boolean,
): PendingTeamInviteJoin | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed) ||
      typeof (parsed as Record<string, unknown>).nickname !== "string" ||
      typeof (parsed as Record<string, unknown>).idempotencyKey !== "string"
    ) {
      return null;
    }
    const pending = parsed as PendingTeamInviteJoin;
    const keys = Object.keys(pending);
    if (
      !keys.every((key) => key === "nickname" || key === "idempotencyKey" || key === "userId") ||
      keys.length < 2 ||
      keys.length > 3 ||
      (pending.userId !== undefined && (typeof pending.userId !== "string" || pending.userId.trim().length === 0))
    ) {
      return null;
    }
    return isNickname(pending.nickname) && pending.idempotencyKey.trim().length > 0
      ? pending
      : null;
  } catch {
    return null;
  }
}

export function teamInviteTokenFromReturnPath(returnPath: string): string | null {
  const match = /^\/join\/team\/([^/]+)$/.exec(returnPath);
  return match && isTeamInviteToken(match[1]!) ? match[1]! : null;
}
